import { EditorControl } from '../../common/components/lexical/editor-control-plugin';
import isHotKey from 'is-hotkey';
import { useCallback, KeyboardEvent } from 'react';
import { getSelectedConversationIndex } from '../helpers/conversation-helpers';
import {
    closeAndClearActiveFilterTabMenu,
    getSelectedConversationsFilterIndex,
} from '../helpers/conversations-filter-helpers';
import { getNextIndex, getPreviousIndex } from '../helpers/array-helpers';
import { useMessagingStore } from './use-messaging-store';

const isToggleUrgentHotKey = isHotKey('mod+u');
const isSelectPreviousConversationHotKey = isHotKey('mod+Up');
const isSelectNextConversationHotKey = isHotKey('mod+Down');
const isSelectPreviousFilterHotKey = isHotKey('mod+Left');
const isSelectNextFilterHotKey = isHotKey('mod+Right');
const isInputCharPress = (event: KeyboardEvent<HTMLDivElement>) => {
    return (
        // event.shiftKey is valid input char (for upper case and punctuations)
        event.key.length === 1 && !event.altKey && !event.ctrlKey && !event.metaKey
    );
};

export const useMessagingPanelHotkeys = ({
    editorControlRef,
    defaultFocusElementRef,
}: {
    editorControlRef: React.MutableRefObject<EditorControl | undefined>;
    defaultFocusElementRef?: React.MutableRefObject<HTMLDivElement | null>;
}) => {
    const {
        conversationListKeys,
        conversationsFilterList,
        isCreatingNewConversation,
        selectedConversationFilter,
        selectedConversationKey,
        setSelectedConversationFilter,
        setSelectedConversationKey,
        setActiveFilterTabMenu,
        toggleAddUrgentMessage,
    } = useMessagingStore();

    const selectPreviousConversation = useCallback(() => {
        const currentIndex = getSelectedConversationIndex(
            conversationListKeys,
            selectedConversationKey
        );
        const previousIndex = getPreviousIndex(conversationListKeys, currentIndex);

        setSelectedConversationKey(conversationListKeys[previousIndex]);
    }, [conversationListKeys, selectedConversationKey, setSelectedConversationKey]);

    const selectNextConversation = useCallback(() => {
        const currentIndex = getSelectedConversationIndex(
            conversationListKeys,
            selectedConversationKey
        );
        const nextIndex = getNextIndex(conversationListKeys, currentIndex);

        setSelectedConversationKey(conversationListKeys[nextIndex]);
    }, [conversationListKeys, selectedConversationKey, setSelectedConversationKey]);

    const selectPreviousFilter = useCallback(() => {
        const currentIndex = getSelectedConversationsFilterIndex(
            conversationsFilterList,
            selectedConversationFilter
        );
        const previousIndex = getPreviousIndex(conversationsFilterList, currentIndex);

        const previousConversationFilter = conversationsFilterList[previousIndex];
        closeAndClearActiveFilterTabMenu({
            filterType: previousConversationFilter?.filterType,
            setActiveFilterTabMenu,
        });
        setSelectedConversationFilter(previousConversationFilter);
    }, [
        conversationsFilterList,
        selectedConversationFilter,
        setSelectedConversationFilter,
        setActiveFilterTabMenu,
    ]);

    const selectNextFilter = useCallback(() => {
        const currentIndex = getSelectedConversationsFilterIndex(
            conversationsFilterList,
            selectedConversationFilter
        );
        const nextIndex = getNextIndex(conversationsFilterList, currentIndex);

        const nextConversationFilter = conversationsFilterList[nextIndex];
        closeAndClearActiveFilterTabMenu({
            filterType: nextConversationFilter?.filterType,
            setActiveFilterTabMenu,
        });
        setSelectedConversationFilter(nextConversationFilter);
    }, [
        conversationsFilterList,
        selectedConversationFilter,
        setSelectedConversationFilter,
        setActiveFilterTabMenu,
    ]);

    const keyDownHandler: React.KeyboardEventHandler<HTMLDivElement> = (event) => {
        if (isCreatingNewConversation) {
            // only allow urgent hot key in new conversation subpanel
            if (isToggleUrgentHotKey(event)) {
                toggleAddUrgentMessage();
            }
            return;
        }

        if (isToggleUrgentHotKey(event)) {
            toggleAddUrgentMessage();
        } else if (isSelectPreviousConversationHotKey(event)) {
            // focus on default element first, then perform left/right/up/down shortcut. otherwise document.activeElement will be set to root body randomly. Other shortcut/keypress do not need focus.
            defaultFocusElementRef?.current?.focus();
            selectPreviousConversation();
        } else if (isSelectNextConversationHotKey(event)) {
            defaultFocusElementRef?.current?.focus();
            selectNextConversation();
        } else if (isSelectPreviousFilterHotKey(event)) {
            defaultFocusElementRef?.current?.focus();
            selectPreviousFilter();
        } else if (isSelectNextFilterHotKey(event)) {
            defaultFocusElementRef?.current?.focus();
            selectNextFilter();
        } else if (isInputCharPress(event)) {
            editorControlRef?.current?.focus();
        }
    };

    return { keyDownHandler };
};
